import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useEffect, useRef } from 'react';
import { $getNearestNodeFromDOMNode, $isElementNode } from 'lexical';

export default function DragDropPlugin() {
  const [editor] = useLexicalComposerContext();

  const handleRef = useRef(null);
  const draggedNodeRef = useRef(null);
  const hoveredElementRef = useRef(null);

  useEffect(() => {
    const editorRoot = editor.getRootElement();
    if (!editorRoot) return;

    createHandle();

    const onMouseMove = (e) => {
      const block = getBlockElement(e.target, editorRoot);

      if (!block) {
        hideHandle();
        return;
      }

      hoveredElementRef.current = block;
      positionHandle(block);
    };

    const onDrop = (e) => {
      e.preventDefault();

      editor.update(() => {
        const targetDOM = hoveredElementRef.current;

        if (!targetDOM) return;

        let targetNode = $getNearestNodeFromDOMNode(targetDOM);

        if (!targetNode) return;

        //  climb to top-level node
        while (
          targetNode.getParent() &&
          targetNode.getParent().getType() !== 'root'
        ) {
          targetNode = targetNode.getParent();
        }

        let draggedNode = draggedNodeRef.current;

        if (!draggedNode) return;

        //  climb dragged node to top-level too
        while (
          draggedNode.getParent() &&
          draggedNode.getParent().getType() !== 'root'
        ) {
          draggedNode = draggedNode.getParent();
        }

        if (draggedNode === targetNode) return;

        targetNode.insertBefore(draggedNode);
      });

      draggedNodeRef.current = null;
    };

    const onDragOver = (e) => {
      e.preventDefault();

      const block = getBlockElement(e.target, editorRoot);

      if (!block) {
        hoveredElementRef.current = null;
        hideHandle();
        return;
      }

      hoveredElementRef.current = block;
      positionHandle(block);
    };

    editorRoot.addEventListener('mousemove', onMouseMove);
    editorRoot.addEventListener('drop', onDrop);
    editorRoot.addEventListener('dragover', onDragOver);

    return () => {
      editorRoot.removeEventListener('mousemove', onMouseMove);
      editorRoot.removeEventListener('drop', onDrop);
      editorRoot.removeEventListener('dragover', onDragOver);

      if (handleRef.current) {
        handleRef.current.remove();
        handleRef.current = null;
      }
    };
  }, [editor]);

  function createHandle() {
    if (handleRef.current) return;

    const handle = document.createElement('div');

    handle.innerHTML = '⋮⋮';

    handle.style.position = 'absolute';
    handle.style.cursor = 'grab';
    handle.style.padding = '4px';
    handle.style.fontSize = '16px';
    handle.style.display = 'none';
    handle.style.zIndex = '1000';

    handle.draggable = true;

    handle.addEventListener('dragstart', (e) => {
      if (e.dataTransfer) {
        try {
          e.dataTransfer.setData('text/plain', '');
        } catch (err) {}
        e.dataTransfer.effectAllowed = 'move';
      }

      editor.update(() => {
        let node = $getNearestNodeFromDOMNode(hoveredElementRef.current);

        if (!node) return;

        // move to top-level node
        while (node.getParent() && node.getParent().getType() !== 'root') {
          node = node.getParent();
        }

        draggedNodeRef.current = node;
      });
    });

    handle.addEventListener('dragend', () => {
      draggedNodeRef.current = null;
      hideHandle();
    });

    document.body.appendChild(handle);

    handleRef.current = handle;
  }

  function positionHandle(block) {
    const rect = block.getBoundingClientRect();

    const handle = handleRef.current;

    handle.style.top = rect.top + window.scrollY + 'px';
    handle.style.left = rect.left + window.scrollX - 24 + 'px';

    handle.style.display = 'block';
  }

  function hideHandle() {
    if (handleRef.current) handleRef.current.style.display = 'none';
  }

  function getBlockElement(target, root) {
    while (target && target !== root) {
      if (target.parentElement === root) {
        return target;
      }
      target = target.parentElement;
    }
    return null;
  }

  return null;
}
